// React + Components
import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link } from 'gatsby';
import Close from './icons/Close';
import {
  CartLineQuantityAdjustButton,
  CartLineProvider,
  CartLineQuantity,
  Money,
} from '@shopify/hydrogen-react';

// Hooks
import { useCart } from '@shopify/hydrogen-react';

// Libs, configs, utils
import classNames from 'classnames';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';

// Context
import Context from '../context/Context';

/* eslint-disable react/prop-types */

const CartLineItem = ({ line }) => {
  const imageUrl = line.merchandise?.image?.url;
  const productTitle = line.merchandise?.product?.title;
  const selectedOption = line.merchandise?.selectedOptions?.[0];
  const priceAmount = line.cost.totalAmount;

  return (
    <CartLineProvider line={line}>
      <article className="cart__product df b--dark-brown mb2">
        <section className="cart__product-image-wrapper br--dark-brown">
          <picture className="cart__product-image db image">
            {imageUrl ? (
              <>
                <source
                  srcSet={`${imageUrl}?w=800&auto=format&q=100`}
                  media="(min-width: 1000px)"
                />
                <source
                  srcSet={`${imageUrl}?w=600&auto=format`}
                  media="(min-width: 600px)"
                />
                <img
                  alt={productTitle || 'Product Image'}
                  src={`${imageUrl}?w=600&auto=format`}
                />
              </>
            ) : (
              <div className="cart__product-image-placeholder">No Image</div>
            )}
          </picture>
        </section>
        <section className="cart__product-desc df fdc">
          <div className="p1">
            <p className="fw--800">{productTitle}</p>
            {selectedOption && (
              <p className="pt1">
                <span className="dib mono--sm uppercase mr1 ">
                  {selectedOption.name}
                </span>
                <span className="dib">{selectedOption.value}</span>
              </p>
            )}
          </div>
          <div className="bt--dark-brown df cart__product-controls">
            <div className="br--dark-brown cart__quantity p1">
              <p className="mono--sm pb1 uppercase">QTY</p>
              <div className="df aie jcb">
                <CartLineQuantity as="p" />
                <div className="cart__quantity-buttons df aic">
                  <CartLineQuantityAdjustButton
                    className="tl pr1"
                    adjust="decrease"
                  >
                    –
                  </CartLineQuantityAdjustButton>

                  <CartLineQuantityAdjustButton
                    adjust="increase"
                    className="tl"
                  >
                    +
                  </CartLineQuantityAdjustButton>
                </div>
              </div>
            </div>
            <div className="cart__price p1">
              <p className="mono--sm pb1 uppercase">Price</p>
              {priceAmount && (
                <Money data={priceAmount} withoutTrailingZeros as="p" />
              )}
            </div>
          </div>
        </section>
      </article>
    </CartLineProvider>
  );
};

const Cart = () => {
  const [cartNote, setCartNote] = useState('');
  const { isCartOpen, setCartOpen } = useContext(Context);

  const {
    totalQuantity,
    lines,
    cost,
    checkoutUrl,
    attributes,
    cartAttributesUpdate,
  } = useCart();

  // Find current attribute values
  const giftWrapAttribute = attributes.find(({ key }) => key === 'Gift Wrap');
  const giftMessageAttribute = attributes.find(
    ({ key }) => key === 'Gift Message'
  );
  const isGift = giftWrapAttribute?.value === 'Yes';
  const currentNoteValue = giftMessageAttribute?.value || ''; // Get current note or empty string

  // Effect to initialize cartNote from attributes
  useEffect(() => {
    // Initialize cartNote state with the current attribute value
    setCartNote(currentNoteValue);
  }, [currentNoteValue]);

  // Effect to clear note when cart is empty
  useEffect(() => {
    if (lines && lines.length === 0) {
      setCartNote('');
      // {{ Updated part: Find the attribute and update if it's not already empty }}
      const giftMessageAttr = attributes.find(
        ({ key }) => key === 'Gift Message'
      );
      // Only update if the attribute exists and has a value
      if (giftMessageAttr && giftMessageAttr.value !== '') {
        cartAttributesUpdate([{ key: 'Gift Message', value: '' }]);
      }
    }
    // {{ Add attributes and cartAttributesUpdate to dependency array }}
  }, [lines, attributes, cartAttributesUpdate]); // Depend on lines, attributes, and the update function

  const goToCheckout = async () => {
    window.analytics && window.analytics.initiateCheckout();
    window.open(checkoutUrl, '_self');
  };

  const closeCart = () => setCartOpen(false);

  const cartRef = useRef(null);

  useEffect(() => {
    if (cartRef.current) {
      if (isCartOpen) {
        disableBodyScroll(cartRef.current);
      } else {
        enableBodyScroll(cartRef.current);
      }
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isCartOpen]);

  const updateAttributes = newAttributes => {
    if (!Array.isArray(newAttributes)) {
      console.error('updateAttributes expects an array.');
      return;
    }
    cartAttributesUpdate(newAttributes);
  };

  return (
    <React.Fragment>
      <div
        ref={cartRef}
        className={classNames('cart__underlay pf fill top bottom left right', {
          open: isCartOpen,
        })}
        onClick={closeCart}
      />
      <aside
        className={classNames('cart__modal pf bl--dark-brown', {
          open: isCartOpen,
        })}
      >
        <header className="cart__header df aic jcb p2">
          <p className="uppercase">Shopping Cart</p>
          <button className="cart__close" onClick={closeCart}>
            <Close />
          </button>
        </header>
        {totalQuantity > 0 ? (
          <React.Fragment>
            <section className="cart__content df fdc p2 ofy--scroll">
              {lines.map(line => (
                <CartLineItem line={line} key={line.id} />
              ))}
            </section>
            <footer className="cart__footer">
              <div className="p-20">
                <label className="flex gap-10">
                  <input
                    type="checkbox"
                    id="gift"
                    name="gift"
                    onChange={e => {
                      const isChecked = e.target.checked;
                      const attrsToUpdate = [
                        { key: 'Gift Wrap', value: isChecked ? 'Yes' : 'No' },
                      ];
                      if (!isChecked) {
                        attrsToUpdate.push({ key: 'Gift Message', value: '' });
                        setCartNote('');
                      }
                      updateAttributes(attrsToUpdate);
                    }}
                    checked={isGift}
                  />
                  <span className="sans--sm">Is this a gift?</span>
                </label>

                {/* Display current note if it exists and isGift is true */}
                {isGift && currentNoteValue && (
                  <p className="mt-5 sans--sm">
                    Current Note: {currentNoteValue}
                  </p>
                )}

                <label className="block mt-10">
                  <textarea
                    disabled={!isGift} // Textarea still disabled if not a gift
                    className={classNames(
                      'p-10 border border-solid sans--sm bg-transparent w-full placeholder:text-black',
                      {
                        'opacity-50 cursor-not-allowed': !isGift,
                      }
                    )}
                    placeholder="Add or update your gift note" // Update placeholder slightly
                    onChange={e => {
                      setCartNote(e.target.value);
                    }}
                    value={cartNote}
                  />
                </label>
                <button
                  // Disable if note is empty OR (it's a gift AND note hasn't changed)
                  disabled={
                    !cartNote || (isGift && cartNote === currentNoteValue)
                  }
                  onClick={() => {
                    // Set Gift Wrap to Yes and update the message
                    updateAttributes([
                      { key: 'Gift Wrap', value: 'Yes' },
                      { key: 'Gift Message', value: cartNote },
                    ]);
                  }}
                  className={classNames('mt-10 sans--sm', {
                    'opacity-50 cursor-not-allowed':
                      !cartNote || (isGift && cartNote === currentNoteValue),
                  })}
                >
                  Save Note
                </button>
              </div>
              <div className="cart__cloverly bt--dark-brown sans--sm" />
              {cost.subtotalAmount.amount && (
                <div className="cart__subtotal p1 pl2 pr2 sans--sm bt--dark-brown x">
                  <span>
                    Subtotal{' '}
                    <Money
                      data={cost.subtotalAmount}
                      withoutTrailingZeros
                      as="span"
                    />
                  </span>
                  <small className="db">
                    (Taxes will be calculated at checkout)
                  </small>{' '}
                </div>
              )}
              <button
                className="product-tile__add-to-cart pt1 pb1 pl2 pr2 sans--md bt--dark-brown x tl fw--800 uppercase"
                onClick={goToCheckout}
              >
                Proceed to checkout
              </button>
            </footer>
          </React.Fragment>
        ) : (
          <section className="cart__empy pt5 p2">
            <p className="cart__empty--title sans--md mb2">
              Your cart is currently empty.
            </p>

            <Link
              className="cart__empty--link link--underline"
              to="/"
              onClick={closeCart}
            >
              Continue Shopping.
            </Link>
          </section>
        )}
      </aside>
    </React.Fragment>
  );
};

export default Cart;
